// Portfolio Administration v1.0 PR 3 §7.2 — click-through detail modal for
// a single Setup catalog item. Hosts the Mark-Complete / Certify / Decline
// / Waive action cluster. Buttons are gated by the caller's permissions
// (Loans.SetupCertify, ADMIN); the server enforces authoritatively.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  useCertifySetupItem,
  useCompleteSetupItem,
  useDeclineSetupItem,
  useWaiveSetupItem,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  loanSetupTierLabel,
  type LoanSetupChecklistItemDto,
} from "@/api/portfolioAdmin/types";
import { formatCalendarDate, useDateFormatter } from "@/utils/datetime";
import { deferredDueDateMessage, isDeferredDueDate } from "./setupCatalogForm";

interface SetupChecklistRowModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
  row: LoanSetupChecklistItemDto;
  catalogTitle: string;
  catalogCode: string;
  catalogTier: string;
  catalogOwnerRole: string;
  catalogDescription?: string | null;
  catalogDueDateFormula?: string | null;
  /** Admin-only: opens the catalog edit modal for this item (the caller
   *  gates on ADMIN and closes this modal first). */
  onEditCatalog?: () => void;
}

export function SetupChecklistRowModal({
  loanId,
  open,
  onClose,
  row,
  catalogTitle,
  catalogCode,
  catalogTier,
  catalogOwnerRole,
  catalogDescription,
  catalogDueDateFormula,
  onEditCatalog,
}: SetupChecklistRowModalProps) {
  const auth = useAuth();
  const toast = useToast();
  const { date } = useDateFormatter();
  const permissions = new Set(auth.user?.permissions ?? []);
  const roleCodes = new Set(auth.user?.roleCodes ?? []);

  const canCertify =
    permissions.has("Loans.SetupCertify") || roleCodes.has("ADMIN");
  const canWaive = roleCodes.has("E1") || roleCodes.has("ADMIN");

  const complete = useCompleteSetupItem(loanId);
  const certify = useCertifySetupItem(loanId);
  const decline = useDeclineSetupItem(loanId);
  const waive = useWaiveSetupItem(loanId);

  const [reason, setReason] = useState("");

  const isDone = row.status === "Complete" || row.status === "Certified";
  const showCertify = canCertify && row.status === "Complete";

  const surfaceError = (err: unknown, fallback: string) => {
    toast.error(
      err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : fallback,
    );
  };

  const onComplete = async () => {
    try {
      await complete.mutateAsync({
        catalogItemId: row.catalogItemId,
        body: { justification: reason || null },
      });
      toast.success("Setup item marked complete.");
      setReason("");
      onClose();
    } catch (err) {
      surfaceError(err, "Could not mark complete.");
    }
  };
  const onCertify = async () => {
    try {
      await certify.mutateAsync(row.catalogItemId);
      toast.success("Setup item certified.");
      onClose();
    } catch (err) {
      surfaceError(err, "Could not certify.");
    }
  };
  const onDecline = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required to decline a setup item.");
      return;
    }
    try {
      await decline.mutateAsync({
        catalogItemId: row.catalogItemId,
        body: { reason },
      });
      toast.success("Setup item declined.");
      setReason("");
      onClose();
    } catch (err) {
      surfaceError(err, "Could not decline.");
    }
  };
  const onWaive = async () => {
    if (!reason.trim()) {
      toast.error("A justification is required to waive a setup item.");
      return;
    }
    try {
      await waive.mutateAsync({
        catalogItemId: row.catalogItemId,
        body: { reason },
      });
      toast.success("Setup item waived.");
      setReason("");
      onClose();
    } catch (err) {
      surfaceError(err, "Could not waive.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${catalogCode} · ${catalogTitle}`}
      description={catalogDescription ?? undefined}
      size="md"
    >
      <div className="flex flex-col gap-space-3" data-testid="setup-row-modal-body">
        <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
          <dt className="text-caption text-gray-mid uppercase tracking-label">Tier</dt>
          <dd className="m-0">{loanSetupTierLabel(catalogTier)}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">Owner role</dt>
          <dd className="m-0">{catalogOwnerRole}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">Status</dt>
          <dd className="m-0" data-testid="setup-row-status">{row.status}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">Due</dt>
          <dd className="m-0">
            {isDeferredDueDate(row.dueDate)
              ? deferredDueDateMessage(catalogDueDateFormula)
              : formatCalendarDate(row.dueDate)}
          </dd>
          {row.completedDate && (
            <>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Completed</dt>
              <dd className="m-0">{date(row.completedDate)}</dd>
            </>
          )}
          {row.certifiedDate && (
            <>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Certified</dt>
              <dd className="m-0">{date(row.certifiedDate)}</dd>
            </>
          )}
        </dl>

        {row.justification && (
          <div className="rounded-md border border-border-light bg-white-warm p-space-2">
            <div className="text-caption text-gray-mid uppercase tracking-label mb-1">
              Previous justification
            </div>
            <p className="text-small text-onyx m-0 whitespace-pre-wrap">
              {row.justification}
            </p>
          </div>
        )}

        <div>
          <label
            htmlFor="setup-reason"
            className="block text-caption text-gray-mid uppercase tracking-label mb-1"
          >
            Reason / justification
          </label>
          <Textarea
            id="setup-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Required for Decline / Waive; optional for Mark Complete."
            data-testid="setup-reason-textarea"
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-space-2">
          {onEditCatalog && (
            <Button
              variant="tertiary"
              onClick={onEditCatalog}
              className="mr-auto"
              data-testid="setup-edit-catalog-item"
            >
              Edit catalog item
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!isDone && (
            <Button
              variant="primary"
              onClick={() => void onComplete()}
              disabled={complete.isPending}
              data-testid="setup-mark-complete"
            >
              Mark complete
            </Button>
          )}
          {showCertify && (
            <Button
              variant="primary"
              onClick={() => void onCertify()}
              disabled={certify.isPending}
              data-testid="setup-certify"
            >
              Certify
            </Button>
          )}
          {!isDone && (
            <Button
              variant="secondary"
              onClick={() => void onDecline()}
              disabled={decline.isPending || !reason.trim()}
              data-testid="setup-decline"
            >
              Decline
            </Button>
          )}
          {canWaive && row.status !== "Waived" && (
            <Button
              variant="secondary"
              onClick={() => void onWaive()}
              disabled={waive.isPending || !reason.trim()}
              data-testid="setup-waive"
            >
              Waive
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
